import { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useScrollMemory } from '../lib/use-scroll-memory';
import { DEMO } from '../lib/demo';
import { useDemo, type DemoAction } from '../lib/demo-tour';
import { useExpediente } from '../lib/hooks';
import { useToast } from '../lib/use-toast';
import { CADENCIA, RIESGO_LABEL, type EstadoExpediente } from '../lib/phase1';
import { Button, Card, Pill, Skeleton } from '../components/ui';
import { FileText, Sparkles } from 'lucide-react';
import { PersonaIcon, valIcon } from '../components/icons';
import ExpedienteActivitySection from '../components/ExpedienteActivity';
import {
  FieldGrid,
  InfoNote,
  ReasonModal,
  RiskGauge,
  StateSpine,
  TabStrip,
  UboTree,
  docTone,
  estadoTone,
  riesgoTone,
  valTone,
} from '../components/compliance';

interface ActionModal {
  estado: EstadoExpediente;
  title: string;
  confirm: string;
  reasons: string[];
  action: string;
  msg: string;
  tone?: 'default' | 'destructive';
  /** When true, also fire a document-request notification (WhatsApp/email). */
  send?: boolean;
}

const ACTIONS: Record<'docs' | 'escalar' | 'rechazar' | 'aprobar', ActionModal> = {
  docs: {
    estado: 'Docs Pendientes',
    title: 'Solicitar documentos',
    confirm: 'Solicitar y enviar',
    reasons: ['Falta certificado de Registro Público', 'Faltan estados financieros', 'Documento ilegible', 'Documento vencido'],
    action: 'solicitó documentos adicionales',
    msg: 'Estado: Docs Pendientes',
    send: true,
  },
  escalar: {
    estado: 'Escalado',
    title: 'Escalar a gerencia',
    confirm: 'Escalar',
    reasons: ['PEP detectado', 'Riesgo alto', 'Coincidencia en lista', 'Requiere Debida Diligencia Ampliada'],
    action: 'escaló a revisión de gerencia',
    msg: 'Escalado a Gerencia',
    tone: 'destructive',
  },
  rechazar: {
    estado: 'Rechazado',
    title: 'Rechazar expediente',
    confirm: 'Rechazar',
    reasons: ['Documentación insuficiente', 'Riesgo inaceptable', 'Coincidencia en lista restrictiva', 'Cliente no contactable'],
    action: 'rechazó el expediente',
    msg: 'Expediente rechazado',
    tone: 'destructive',
  },
  aprobar: {
    estado: 'Activo',
    title: 'Aprobar expediente',
    confirm: 'Aprobar',
    reasons: ['Cumple requisitos de Ley 23', 'Riesgo aceptable', 'Documentación completa y vigente'],
    action: 'aprobó el expediente → Activo',
    msg: '✓ Expediente aprobado → Activo',
  },
};

const WEIGHT_CLS: Record<string, string> = {
  CRÍTICO: 'bg-tone-red-bg text-tone-red',
  ALTO: 'bg-tone-amber-bg text-tone-amber',
  MEDIO: 'bg-tone-blue-bg text-tone-blue',
  BAJO: 'bg-primary/10 text-primary-dark',
};

/** Performs the tour's "live actions" on this screen without reshuffling hooks above. */
function DemoActions({ onRun, onRisk }: { onRun: () => void; onRisk: () => void }) {
  const demo = useDemo();
  useEffect(() => {
    if (!demo.playing) return;
    const a: DemoAction | null = demo.action;
    if (a === 'runValidations') onRun();
    else if (a === 'showRiskTab') onRisk();
  }, [demo.playing, demo.action]);
  return null;
}

export default function RevisionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { data: exp, isLoading } = useExpediente(id);

  const [tab, setTab] = useState('datos');
  const [valRunning, setValRunning] = useState(false);
  const [valDone, setValDone] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState<ActionModal | null>(null);
  const [channels, setChannels] = useState({ whatsapp: true, email: true });
  const [aiAnswer, setAiAnswer] = useState<{ text: string; source: string } | null>(null);
  const [aiBusy, setAiBusy] = useState(false);

  const scrollMemory = useScrollMemory();
  // Restore the scroll position for the newly-active tab. These tabs swap
  // content in place on one scrolling page, and shorter panels let the browser
  // clamp scrollY near the top — restoring keeps the user where they were.
  useLayoutEffect(() => {
    scrollMemory.restore(tab);
  }, [tab]);

  if (isLoading) {
    return (
      <div>
        <div className="mb-4 flex items-center gap-1.5">
          <Skeleton className="h-3 w-28 rounded" />
          <span className="text-[11px] text-ink-faint">/</span>
          <Skeleton className="h-3 w-20 rounded" />
        </div>
        <Card className="mb-4">
          <div className="flex flex-wrap items-start gap-4">
            <Skeleton className="h-14 w-14 rounded-2xl" />
            <div className="min-w-0 flex-1">
              <Skeleton className="h-7 w-64 rounded" />
              <div className="mt-2 flex flex-wrap gap-2">
                <Skeleton className="h-4 w-32 rounded" />
                <Skeleton className="h-4 w-20 rounded" />
                <Skeleton className="h-5 w-24 rounded-full" />
              </div>
            </div>
          </div>
          <Skeleton className="mt-5 h-8 w-full rounded-xl" />
        </Card>
        <div className="mb-5 flex gap-1 border-b border-line">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-20 rounded-t" />
          ))}
        </div>
        <div className="grid gap-2.5">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }
  if (!exp) return null;

  const refresh = () =>
    qc.invalidateQueries({ queryKey: ['expedientes'] }).then(() =>
      qc.invalidateQueries({ queryKey: ['expedientes', id] })
    );

  async function runVals() {
    if (!exp) return;
    setTab('vals');
    setValRunning(true);
    setValDone(0);
    try {
      // Screen the client against the sanctions/watchlists (OFAC · ONU · UE · Reino
      // Unido · Canadá). The result drives the "Listas" row and the risk score.
      await api.runValidation('sanctions', {
        subjectType: exp.persona,
        subjectId: exp.id,
        fullName: exp.name,
        ...(exp.docId ? { idNumber: exp.docId } : {}),
      });
      await refresh();
      toast({ message: 'Validaciones completadas — evidencia adjunta al expediente', kind: 'success' });
    } catch (err) {
      toast({
        message: err instanceof Error ? err.message : 'No se pudieron ejecutar las validaciones',
        kind: 'error',
      });
    } finally {
      setValRunning(false);
      setValDone(-1);
    }
  }

  async function explainRisk() {
    if (!exp) return;
    setAiBusy(true);
    try {
      const r = await api.assistantExplainRisk(exp.id);
      setAiAnswer({ text: r.answer, source: r.source });
    } catch {
      toast({ message: 'No se pudo generar la explicación.', kind: 'error' });
    } finally {
      setAiBusy(false);
    }
  }

  async function transition(estado: EstadoExpediente, action: string, msg: string) {
    if (!exp) return;
    setBusy(true);
    try {
      await api.setExpedienteEstado(exp.id, estado, action);
      await refresh();
      toast({ message: msg, kind: 'success' });
    } catch (err) {
      toast({
        message: err instanceof Error ? err.message : 'No se pudo cambiar el estado del expediente',
        kind: 'error',
      });
    } finally {
      setBusy(false);
    }
  }

  /** Solicitar documentos: fire the WhatsApp/email notification, then move state. */
  async function requestDocs(m: ActionModal, reason: string) {
    if (!exp) return;
    // Only offer channels the expediente actually has contact info for — the
    // backend rejects (400) WhatsApp without a phone or email without an email.
    const selected = [
      ...(channels.whatsapp && exp.phone ? ['whatsapp'] : []),
      ...(channels.email && exp.email ? ['email'] : []),
    ];
    if (selected.length === 0) {
      toast({
        message:
          !exp.phone && !exp.email
            ? 'Este expediente no tiene teléfono ni correo. Agrega un dato de contacto para solicitar documentos.'
            : 'Seleccione al menos un canal disponible (WhatsApp o correo)',
        kind: 'error',
      });
      return;
    }
    setBusy(true);
    try {
      const res = await api.requestDocuments(exp.id, { channels: selected });
      await api.setExpedienteEstado(exp.id, m.estado, `${m.action} (motivo: ${reason})`);
      await refresh();
      const channelLabels = selected.map((c) => (c === 'whatsapp' ? 'WhatsApp' : 'correo')).join(' y ');
      const kind = res.status === 'failed' ? 'error' : 'success';
      const label =
        res.status === 'sent'
          ? `Solicitud enviada por ${channelLabels}`
          : res.status === 'partial'
          ? `Solicitud parcialmente enviada — un canal falló. Revise la configuración.`
          : res.status === 'failed'
          ? 'No se pudo enviar la solicitud. Revise la configuración de notificaciones.'
          : 'Solicitud registrada';
      toast({ message: label, kind });
    } catch (err) {
      toast({
        message: err instanceof Error ? err.message : 'Error al enviar la solicitud',
        kind: 'error',
      });
    } finally {
      setBusy(false);
    }
  }

  const tabs = [
    { key: 'datos', label: 'Datos' },
    { key: 'docs', label: 'Documentos', count: exp.docs.length },
    { key: 'vals', label: 'Validaciones', count: exp.validations.length },
    { key: 'riesgo', label: 'Riesgo' },
    ...(exp.ubo ? [{ key: 'ubo', label: 'Beneficiarios', count: exp.ubo.length }] : []),
    { key: 'hist', label: 'Historial' },
  ];

  return (
    <div>
      <DemoActions onRun={() => void runVals()} onRisk={() => setTab('riesgo')} />
      <div className="mb-4 flex items-center gap-1.5 text-[11px] text-ink-faint">
        <span className="font-semibold uppercase tracking-wider text-primary-dark">Cumplimiento</span>
        <span>/</span>
        <button
          onClick={() => navigate('/revision')}
          className="hover:text-ink transition-colors"
        >
          Expedientes
        </button>
      </div>

      <Card className="mb-4">
        <div className="flex flex-wrap items-start gap-4">
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-primary/10">
            <PersonaIcon persona={exp.persona} className="h-7 w-7 text-primary-dark" />
          </span>
          <div className="min-w-0">
            <h1 className="font-serif text-2xl font-medium">{exp.name}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-ink-soft">
              <span className="font-mono text-ink-faint">{exp.id}</span>
              <span>·</span>
              <span>{exp.kind}</span>
              <span>·</span>
              <span className="font-semibold text-ink">{exp.sector}</span>
              <Pill tone={estadoTone(exp.estado)}>{exp.estado}</Pill>
              <Pill tone={riesgoTone(exp.riesgo)}>Riesgo {RIESGO_LABEL[exp.riesgo]}</Pill>
              {exp.pep && <Pill tone="red">PEP</Pill>}
            </div>
          </div>
          <div data-tour="rev-decision" className="flex w-full flex-wrap gap-2 sm:ml-auto sm:w-auto">
            <Button variant="outline" onClick={runVals} disabled={valRunning || busy}>
              Ejecutar validaciones
            </Button>
            <Button
              variant="outline"
              disabled={busy || (!exp.phone && !exp.email)}
              title={
                !exp.phone && !exp.email
                  ? 'El expediente no tiene teléfono ni correo registrados'
                  : undefined
              }
              onClick={() => setModal(ACTIONS.docs)}
            >
              Solicitar docs
            </Button>
            <Button variant="outline" disabled={busy} onClick={() => setModal(ACTIONS.escalar)}>
              Escalar
            </Button>
            <Button variant="destructive" disabled={busy} onClick={() => setModal(ACTIONS.rechazar)}>
              Rechazar
            </Button>
            <Button disabled={busy} onClick={() => setModal(ACTIONS.aprobar)}>
              Aprobar
            </Button>
          </div>
        </div>
        <div className="mt-5">
          <StateSpine estado={exp.estado} />
        </div>
      </Card>

      <TabStrip
        tabs={tabs}
        active={tab}
        onChange={(k) => {
          scrollMemory.remember(tab);
          setTab(k);
        }}
      />

      {tab === 'datos' && <FieldGrid fields={exp.fields} />}

      {tab === 'docs' && (
        <div className="grid gap-2.5">
          {exp.docs.length === 0 && (
            <p className="text-sm text-ink-faint">Aún no se han cargado documentos.</p>
          )}
          {exp.docs.map((d, i) => (
            <div key={i} className="flex items-center gap-3.5 rounded-xl border border-line bg-panel px-4 py-3.5">
              <span className="grid h-10 w-9 place-items-center rounded-lg border border-line bg-canvas text-ink-soft">
                <FileText className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-sm font-bold">{d.name}</div>
                <div className="font-mono text-[10.5px] text-ink-faint">
                  {d.category}
                  {d.expiry ? ` · vence ${d.expiry}` : ''}
                </div>
              </div>
              <span className="ml-auto">
                <Pill tone={docTone(d.estado)}>{d.estado}</Pill>
              </span>
            </div>
          ))}
        </div>
      )}

      {tab === 'vals' && (
        <div data-tour="rev-validations" className="grid gap-2.5">
          {exp.validations.map((v, i) => {
            const running = valRunning && i >= valDone;
            const VI = valIcon(v.key);
            return (
              <div key={v.key} className="flex items-center gap-3.5 rounded-xl border border-line bg-panel px-4 py-3.5">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-canvas">
                  <VI className="h-5 w-5 text-ink-soft" strokeWidth={1.75} />
                </span>
                <div>
                  <div className="text-sm font-bold">{v.title}</div>
                  <div className="text-[11.5px] text-ink-soft">{v.provider}</div>
                  {v.detail && <div className="text-[11px] text-ink-faint">{v.detail}</div>}
                </div>
                <span className="ml-auto flex items-center gap-2.5">
                  {running ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-line-strong border-t-primary" />
                      <Pill tone="slate">Ejecutando…</Pill>
                    </>
                  ) : (
                    <Pill tone={valTone(v.estado)}>{v.result}</Pill>
                  )}
                </span>
              </div>
            );
          })}
          <p className="mt-1 font-mono text-[11px] text-ink-faint">
            Las validaciones corren en paralelo (SVI · APC · screening · OCR · Registro). Cada
            resultado queda como evidencia en el expediente.
          </p>
        </div>
      )}

      {tab === 'riesgo' && (
        <div data-tour="rev-risk" className="grid gap-6 md:grid-cols-[240px_1fr]">
          <RiskGauge score={exp.score} riesgo={exp.riesgo} cadence={CADENCIA[exp.riesgo]} />
          <div>
            <div className="mb-2 font-mono text-[10px] uppercase tracking-wide text-ink-faint">
              Factores ponderados
            </div>
            <Card className="p-0">
              {exp.factors.length === 0 && (
                <p className="px-5 py-4 text-sm text-ink-faint">
                  Sin evaluar todavía — ejecuta las validaciones para calcular el riesgo.
                </p>
              )}
              {exp.factors.map((f, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 border-b border-line px-5 py-3 last:border-0"
                >
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{f.name}</div>
                    <div className="font-mono text-[10px] text-ink-faint">{f.source}</div>
                  </div>
                  <span className={`rounded-md px-2 py-1 font-mono text-[10px] font-semibold ${WEIGHT_CLS[f.weight]}`}>
                    {f.weight}
                  </span>
                </div>
              ))}
            </Card>
            {exp.pep && (
              <div className="mt-4">
                <InfoNote tone="amber">
                  ⚠️
                  <div>
                    <b>PEP detectado.</b> Fuerza riesgo Alto + revisión manual. Requiere informe de
                    Debida Diligencia Ampliada (EDD) y aprobación de Gerencia + GM.
                  </div>
                </InfoNote>
              </div>
            )}

            {/* Scoped AI assistant — explains this client's risk from its factors. */}
            <div className="mt-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="font-mono text-[10px] uppercase tracking-wide text-ink-faint">
                  Asistente IA
                </span>
                <Button variant="outline" onClick={() => void explainRisk()} disabled={aiBusy}>
                  <Sparkles className="h-4 w-4" strokeWidth={2} />
                  {aiBusy ? 'Generando…' : 'Explicar con IA'}
                </Button>
              </div>
              {aiAnswer && (
                <Card>
                  <div className="mb-2">
                    <Pill tone={aiAnswer.source === 'ai' ? 'blue' : 'slate'}>
                      {aiAnswer.source === 'ai' ? 'Generado por IA' : 'Resumen automático'}
                    </Pill>
                  </div>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-soft">
                    {aiAnswer.text}
                  </p>
                </Card>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === 'ubo' && exp.ubo && (
        <div className="grid gap-4">
          <UboTree name={exp.name} persona={exp.persona} ubo={exp.ubo} />
          <InfoNote tone="blue">
            💡
            <div>
              Cada persona natural en la estructura hereda el set completo de validaciones
              (identidad, listas, fuente de fondos, fuentes abiertas). Una persona jurídica dispara{' '}
              <b>N sub-procesos KYC en cascada</b>.
            </div>
          </InfoNote>
        </div>
      )}

      {tab === 'hist' && <ExpedienteActivitySection expedienteId={exp.id} />}

      <div className="mt-5 flex items-center justify-between gap-4">
        {DEMO && (
          <p className="font-mono text-[11px] text-ink-faint">
            Entorno demo · datos de demostración.
          </p>
        )}
        <Button variant="ghost" onClick={() => navigate(`/expedientes/${exp.id}/dossier`)}>
          Exportar expediente (PDF)
        </Button>
      </div>

      {modal && (
        <ReasonModal
          open
          title={modal.title}
          confirmLabel={modal.confirm}
          reasons={modal.reasons}
          tone={modal.tone}
          extra={
            modal.send ? (
              <div>
                <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wide text-ink-faint">
                  Enviar por
                </div>
                <div className="flex gap-2">
                  {(
                    [
                      ['whatsapp', 'WhatsApp', !!exp.phone],
                      ['email', 'Correo', !!exp.email],
                    ] as const
                  ).map(([key, label, available]) => (
                    <button
                      key={key}
                      type="button"
                      disabled={!available}
                      onClick={() => setChannels((c) => ({ ...c, [key]: !c[key] }))}
                      title={available ? undefined : 'Sin dato de contacto en el expediente'}
                      className={
                        'flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition ' +
                        (!available
                          ? 'cursor-not-allowed border-line bg-canvas text-ink-faint opacity-60'
                          : channels[key]
                          ? 'border-primary bg-primary/10 text-primary-dark'
                          : 'border-line-strong bg-canvas text-ink-soft')
                      }
                    >
                      {available && channels[key] ? '✓ ' : ''}
                      {label}
                    </button>
                  ))}
                </div>
                {!exp.phone && !exp.email ? (
                  <p className="mt-1.5 text-[11px] font-semibold text-tone-red">
                    Este expediente no tiene teléfono ni correo registrados. Agrega un dato de
                    contacto para poder solicitar documentos.
                  </p>
                ) : !exp.phone || !exp.email ? (
                  <p className="mt-1.5 font-mono text-[10px] text-ink-faint">
                    {!exp.phone ? 'Sin teléfono — WhatsApp no disponible.' : 'Sin correo — correo no disponible.'}
                  </p>
                ) : null}
                <p className="mt-1.5 font-mono text-[10px] text-ink-faint">
                  Modo prueba: todo envío se redirige al teléfono/correo configurados.
                </p>
              </div>
            ) : undefined
          }
          onClose={() => setModal(null)}
          onConfirm={(reason) => {
            const m = modal;
            setModal(null);
            if (m.send) void requestDocs(m, reason);
            else void transition(m.estado, `${m.action} (motivo: ${reason})`, m.msg);
          }}
        />
      )}
    </div>
  );
}
